import { NormalButton, CustomButton } from '@/shared/ui/components';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { Timeline } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useLabelInformation } from '../../model/labelInformation';
import DownloadLabelIcon from './assets/download-label-icon.svg';
import type { LabelInformationProps } from './labelInformation.types';
import './styles.scss';

/**
 * Renders one return shipping label of an RMA ticket: tracking number linked to the carrier's tracking page, shipped date, estimated arrival, the downloadable label with carrier logo, service level and cost, the device ids on the label and the tracking timeline. Missing values fall back to the double-hyphen bullet, dates use MM/DD/YYYY, an UNKNOWN status shows as Pre-transit, and refresh calls the tracking handler with the transaction and ticket ids while skeletoning the timeline.
 *
 * @param props - Label data including transaction, rate, shipmentDate, deviceLabel, index, ticket id and the tracking refresh handler.
 * @returns The shipping label panel with its device list and tracking timeline.
 */
const LabelInformation = (props: LabelInformationProps) => {
  const {
    state,
    setState,
    formatLocation,
    onDownloadLink,
    shippingLabel,
    indexText,
    orderedTrackingHistory,
  } = useLabelInformation(props);

  return (
    <div className="label-information-wrapper">
      <div className="label-information-table">
        <div className="label-information">
          <div className="title-row">
            <span>{t`Label ${indexText}`}</span>
          </div>
          <div className="label-information-content">
            <div className="left-column">
              <div className="item-row">
                <div className="title">{`${t`Tracking number`}:`}</div>
                <div className="content">
                  <a
                    className="number"
                    href={props.transaction?.trackingUrlProvider || '#'}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {props.transaction?.trackingNumber || DOUBLE_HYPHEN_BULLET}
                  </a>
                </div>
              </div>
              <div className="item-row">
                <div className="title">{`${t`Shipped date`}:`}</div>
                <div className="content">
                  <div className="date">
                    {props.shipmentDate
                      ? dayjs(props.shipmentDate).format('MM/DD/YYYY')
                      : DOUBLE_HYPHEN_BULLET}
                  </div>
                </div>
              </div>
              <div className="item-row">
                <div className="title">{`${t`Estimated arrival`}:`}</div>
                <div className="content">
                  <div className="date">
                    {props.transaction?.eta
                      ? dayjs(props.transaction?.eta).format('MM/DD/YYYY')
                      : DOUBLE_HYPHEN_BULLET}
                  </div>
                </div>
              </div>
            </div>
            <div className="right-column">
              <div className="item-row">
                <div className="title">{`${t`Label`}:`}</div>
                <div className="content">
                  <div className="shipping-label">
                    <NormalButton
                      isText
                      buttonName={shippingLabel}
                      id={`${shippingLabel}-${props.index}`}
                      className="btn-download-label"
                      onClick={() => {
                        onDownloadLink(shippingLabel, props.index);
                      }}
                    />
                    <CustomButton
                      id="download-link-btn"
                      className="download-icon"
                      icon={<ReactSVG src={DownloadLabelIcon} />}
                      ariaLabel={t`Download label`}
                      type="tertiary"
                      onClick={() => {
                        onDownloadLink(shippingLabel, props.index);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="item-row">
                <div className="title">{`${t`Label details`}:`}</div>
                <div className="content">
                  <div className="label-details">
                    <div className="delivery-unit">
                      <img src={props.rate?.carrierLogo200} alt="Carrier logo" />
                      <span>{`${props.rate?.carrier} ${props.rate?.serviceLevelName}`}</span>
                    </div>
                    <div className="cost">{`$${(props.rate?.amount || 0).toFixed(2)}`}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="device-timeline">
          <div className="device">
            <div className="title-row">
              {t`Device`} ({props.deviceLabel?.length})
            </div>
            <div className="content">
              {!_.isEmpty(props.deviceLabel) ? (
                _.map(props.deviceLabel, (deviceId) => (
                  <div className="device-id-tag">{deviceId}</div>
                ))
              ) : (
                <div className="content-empty">{t`There is no data to display`}</div>
              )}
            </div>
          </div>
          <div className="timeline">
            <div className="title-row">
              <span>{t`Timeline`}</span>
              <NormalButton
                id="refresh-btn"
                isText
                isBold
                buttonName={t`Refresh`}
                onClick={() => {
                  setState({ isRefreshingTracking: true });
                  props.handleRefreshTracking({
                    transactionId: props.transaction?.id,
                    ticketId: props.id,
                    index: props.index,
                    callback: () => {
                      setState({ isRefreshingTracking: false });
                    },
                  });
                }}
              />
            </div>
            <div className="timeline-box">
              {!_.isEmpty(orderedTrackingHistory) ? (
                <div className="date-container">
                  {state.isRefreshingTracking ? (
                    <Skeleton
                      style={{
                        width: '100%',
                        height: '100%',
                        minHeight: 100,
                      }}
                    />
                  ) : (
                    <Timeline mode="left" aria-label={t`Tracking history`}>
                      {orderedTrackingHistory?.map((item) => (
                        <Timeline.Item
                          dot={<div className="dot-timeline" />}
                          key={`${item.status}-${item.statusDate}-${item.statusDetails}`}
                        >
                          <div className="timeline-title">
                            {item.status
                              ? item.status === 'UNKNOWN'
                                ? 'Pre-transit'
                                : _.upperFirst(_.lowerCase(item.status))
                              : DOUBLE_HYPHEN_BULLET}
                          </div>
                          <div className="status-bar">
                            <span>
                              {item.statusDate
                                ? dayjs(item.statusDate).format('MM/DD/YYYY')
                                : DOUBLE_HYPHEN_BULLET}
                            </span>
                            <div className="address">{formatLocation(item.location)}</div>
                            <div className="status-detail">{item.statusDetails}</div>
                          </div>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  )}
                </div>
              ) : (
                <div className="timeline-empty">{t`There is no data to display`}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

LabelInformation.defaultProps = {
  rate: undefined,
  trackingHistory: undefined,
  transaction: undefined,
  filePDF: undefined,
  stateCA: [],
  stateUS: [],
  country: [],
  deviceLabel: [],
};

export default LabelInformation;
